/** Shapes of the core platform API (people, groups, modules). Mirrors back/app/platform/schemas.py. */

/** reviewer: invited only to review questions (no institution). */
export type Role = 'admin' | 'coordinator' | 'teacher' | 'student' | 'reviewer';
/** Roles an institutional address proves (coordinators and admins are named by hand). */
export type SignupRole = 'student' | 'teacher';

export const ROLE_LABEL: Record<Role, string> = {
  admin: 'Administração',
  coordinator: 'Coordenação',
  teacher: 'Professor(a)',
  student: 'Estudante',
  reviewer: 'Revisor(a)',
};

export interface InstitutionRef {
  id: string;
  name: string;
}

/**
 * Which institutional addresses belong to the institution's students or teachers:
 * `pattern` (a regular expression) is matched against the part before the @.
 */
export interface EmailRule {
  role: SignupRole;
  domain: string;
  pattern: string;
  example: string;
  /** The part before the @ is the student's RA. */
  registration: boolean;
}

/**
 * A single address an admin accepts as institutional, for someone without the school's
 * address: typed on the profile step, it needs no confirmation.
 */
export interface AllowedEmail {
  email: string;
  role: SignupRole;
}

export interface BlockedInstitution extends InstitutionRef {
  /** pending | expired | ended | canceled | none | blocked (see billing/labels.ts) */
  status: string;
}

export interface Institution extends InstitutionRef {
  emailRules: EmailRule[];
  /** Admins only (empty on the profile step's options). */
  allowedEmails: AllowedEmail[];
  isActive: boolean;
  students: number;
  teachers: number;
  coordinators: number;
  /** Admins only: access right now (courtesy, trial, active, overdue...). */
  accessStatus?: string | null;
  hasAccess?: boolean;
  planKey?: string | null;
  /** founding | admin | self_service */
  origin?: string | null;
}

export interface CourseRef {
  id: string;
  name: string;
}

export interface Course extends CourseRef {
  isActive: boolean;
  students: number;
}

export interface SignupOptions {
  institutions: Institution[];
  courses: CourseRef[];
}

export interface GroupRef {
  id: string;
  name: string;
  code: string | null;
}

export interface Person {
  id: string;
  email: string;
  name: string | null;
  photoUrl: string | null;
  role: Role;
  registration: string | null;
  emailVerified: boolean;
  /** Institutional address (e.g. @cps.sp.gov.br), confirmed by an e-mailed link. */
  institutionalEmail: string | null;
  institutionalEmailVerified: boolean;
  /** Role asked for (teacher, by confirming @cps.sp.gov.br) and waiting for the coordination. */
  requestedRole: Role | null;
  institutions: InstitutionRef[];
  /** Students' course (e.g. Engenharia de Software). */
  course: CourseRef | null;
  /** Contact details: only to the person, the coordination and the administration. */
  phone?: string | null;
  /** Personal address, only stored: people sign in with whichever Google account they like. */
  personalEmail?: string | null;
  /** Studies on their own, with no institution (e.g. for the ENEM). */
  independent?: boolean;
  isActive: boolean;
  hasPassword: boolean;
  hasGoogle: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  groups: GroupRef[];
}

export interface ModuleInfo {
  key: string;
  name: string;
  description: string;
  icon: string;
  version: string;
  enabled: boolean;
  features: string[];
  updatedAt: string | null;
}

/**
 * Opt-ins (LGPD), all off by default: Prof. Rodrigo Bossini's newsletter (AI, dev, cloud,
 * DevOps), his products and courses, and third parties' products and courses, by e-mail and/or
 * phone. `phone` is required when any phone option is on.
 */
export interface Consent {
  newsEmail: boolean;
  newsPhone: boolean;
  offersEmail: boolean;
  offersPhone: boolean;
  partnersEmail: boolean;
  partnersPhone: boolean;
  phone?: string | null;
}

export type ReviewerStatus = 'invited' | 'pending' | 'active' | 'suspended';

export interface Me {
  user: Person;
  /** Seeing Lousa as this student (read-only): the admin or coordinator actually signed in. */
  viewer?: { id: string; name: string | null; email: string } | null;
  /** Invited to review questions (any role): where the reviewer profile stands. */
  reviewer?: { status: ReviewerStatus } | null;
  /** False until the institutional e-mail is confirmed: only the profile step is shown. */
  profileComplete: boolean;
  /** True while the requested role waits for approval: only the waiting screen is shown. */
  approvalPending: boolean;
  /** Admins and coordinators: how many people (of their institutions) wait for approval. */
  pendingApprovals: number;
  /** Institutions whose data this person may look at (admins: all of them). */
  institutions: InstitutionRef[];
  /** TEST_EMAILS: chooses to be a student or a teacher on each sign-in. */
  testAccount: boolean;
  /** None of the person's institutions may use the platform now (unpaid, canceled, cut off). */
  accessBlocked: boolean;
  /** The person's institutions without access right now. */
  blockedInstitutions: BlockedInstitution[];
  /** Coordinators: sees the plan and pays (Plano e pagamentos). */
  managesBilling: boolean;
  /** Free use (courtesy, trial): the AdSense publisher and ad unit; null: no ads. */
  ads?: { client: string; slot: string } | null;
  /** What the person agreed to receive (changed in the page footer). */
  consent?: Consent;
  /** False until the person answers the opt-in question once: it then opens by itself, once. */
  consentAnswered?: boolean;
  modules: ModuleInfo[];
  app: {
    name: string;
    version: string;
    googleEnabled: boolean;
    passwordLogin: boolean;
  };
}

export interface Page<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ImportResult {
  dryRun: boolean;
  total: number;
  created: number;
  updated: number;
  skipped: number;
  errors: { row: number; message: string; content: string }[];
  preview: Record<string, unknown>[];
}

export interface Group {
  id: string;
  name: string;
  code: string | null;
  term: string | null;
  description: string | null;
  archived: boolean;
  institutionId: string;
  memberCount: number;
  createdAt: string;
}

export interface GroupDetail extends Group {
  members: Person[];
}

export interface AuditEntry {
  id: string;
  action: string;
  summary: string;
  entityType: string | null;
  entityId: string | null;
  createdAt: string;
  actorName: string | null;
  actorEmail: string | null;
}

export interface Overview {
  users: number;
  students: number;
  teachers: number;
  coordinators: number;
  admins: number;
  institutions: number;
  groups: number;
  activeLast7Days: number;
  modulesEnabled: number;
  modulesTotal: number;
  database: string;
  version: string;
}
